import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

import { ReleaseFlowEditorWithProvider } from '../components/ReleaseFlow/ReleaseFlowEditor';
import { useReleaseFlow, useReleaseFlowEditor } from '../hooks/useReleaseFlow';
import { useRoles } from '../auth/useRoles';
import type { 
  ReleaseFlowNodeDto, 
  ReleaseFlowEdgeDto, 
  CreateReleaseFlowDto, 
  UpdateReleaseFlowDto 
} from '../types/releaseFlow';

export const ReleaseFlowEditorPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { canWrite } = useRoles();
  const isEditing = !!id;

  const { data: releaseFlow, isLoading, error } = useReleaseFlow(id);
  const { 
    saveReleaseFlow, 
    validateExistingReleaseFlow,
    isSaving, 
    isValidating 
  } = useReleaseFlowEditor();

  const [releaseFlowName, setReleaseFlowName] = useState('');
  const [releaseFlowDescription, setReleaseFlowDescription] = useState('');
  const [validationResult, setValidationResult] = useState<any>(null);
  const [showValidation, setShowValidation] = useState(false);

  // Initialize form when data loads
  React.useEffect(() => {
    if (releaseFlow) {
      setReleaseFlowName(releaseFlow.name);
      setReleaseFlowDescription(releaseFlow.description || '');
    }
  }, [releaseFlow]);

  const handleSave = useCallback(
    async (data: { nodes: ReleaseFlowNodeDto[]; edges: ReleaseFlowEdgeDto[] }) => {
      try {
        if (!releaseFlowName.trim()) {
          toast.error('Please enter a release flow name');
          return;
        }

        // Helper function to ensure valid GUID format for node IDs
        const ensureGuidFormat = (id: string): string => {
          // Check if it's already a valid GUID format (8-4-4-4-12 characters)
          const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
          return guidRegex.test(id) ? id : crypto.randomUUID();
        };

        // Create a mapping of old IDs to new GUIDs
        const nodeIdMapping = new Map<string, string>();
        data.nodes.forEach(node => {
          const newId = ensureGuidFormat(node.id);
          nodeIdMapping.set(node.id, newId);
        });

        const flowData = {
          name: releaseFlowName,
          description: releaseFlowDescription,
          isActive: true,
          nodes: data.nodes.map(node => ({
            clientId: node.id, // Use the ReactFlow node ID as the client ID
            name: node.name,
            description: node.description,
            nodeType: node.nodeType,
            pipelineDefinitionId: node.nodePipelineLink?.pipelineDefinitionId,
            configuration: node.configuration || '{}',
            isActive: node.isActive,
            positionX: node.positionX,
            positionY: node.positionY,
            width: node.width,
            height: node.height,
            backgroundColor: node.backgroundColor,
            borderColor: node.borderColor,
          })),
          edges: data.edges.map(edge => ({
            sourceNodeClientId: edge.sourceNodeId, // Use ReactFlow IDs directly
            targetNodeClientId: edge.targetNodeId, // Use ReactFlow IDs directly
            condition: edge.condition,
            orderIndex: edge.orderIndex,
            isActive: edge.isActive,
            edgeType: edge.edgeType,
            label: edge.label,
            animated: edge.animated,
            style: edge.style,
            markerEnd: edge.markerEnd,
          })),
        };

        if (isEditing && releaseFlow) {
          // For updates, we need to map node IDs properly and use different structure
          const updateData: UpdateReleaseFlowDto & { id: string } = {
            id: releaseFlow.id,
            name: releaseFlowName,
            description: releaseFlowDescription,
            isActive: true,
            nodes: data.nodes.map(node => ({
              id: node.id, // Use the actual node ID for updates
              name: node.name,
              description: node.description,
              nodeType: node.nodeType,
              pipelineDefinitionId: node.nodePipelineLink?.pipelineDefinitionId,
              configuration: node.configuration || '{}',
              isActive: node.isActive,
              positionX: node.positionX,
              positionY: node.positionY,
              width: node.width,
              height: node.height,
              backgroundColor: node.backgroundColor,
              borderColor: node.borderColor,
            })),
            edges: data.edges.map(edge => ({
              id: edge.id, // Use the actual edge ID for updates
              sourceNodeId: edge.sourceNodeId,
              targetNodeId: edge.targetNodeId,
              condition: edge.condition,
              orderIndex: edge.orderIndex,
              isActive: edge.isActive,
              edgeType: edge.edgeType,
              label: edge.label,
              animated: edge.animated,
              style: edge.style,
              markerEnd: edge.markerEnd,
            })),
          };
          await saveReleaseFlow(updateData);
          toast.success('Release flow updated successfully');
        } else {
          const createData: CreateReleaseFlowDto = flowData;
          const result = await saveReleaseFlow(createData);
          toast.success('Release flow created successfully');
          navigate(`/release-flows/${result.id}`);
        }
      } catch (error) {
        console.error('Error saving release flow:', error);
        toast.error('Failed to save release flow');
      }
    },
    [
      releaseFlowName,
      releaseFlowDescription,
      isEditing,
      releaseFlow,
      saveReleaseFlow,
      navigate,
    ]
  );

  const handleValidate = useCallback(async () => {
    try {
      setShowValidation(true);
      
      if (isEditing && releaseFlow) {
        const result = await validateExistingReleaseFlow(releaseFlow.id);
        setValidationResult(result);
      } else {
        // For new release flows, we'd need the current editor state
        // This is a simplified implementation
        toast('Validation for new flows requires saving first');
      }
    } catch (error) {
      console.error('Error validating release flow:', error);
      toast.error('Failed to validate release flow');
    }
  }, [isEditing, releaseFlow, validateExistingReleaseFlow]);

  const handleBack = () => {
    navigate('/release-flows');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!canWrite) {
    navigate('/release-flows');
    return null;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Release Flow</h1>
          <p className="text-gray-600 mb-4">
            {error instanceof Error ? error.message : 'An unexpected error occurred'}
          </p>
          <button
            onClick={handleBack}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
          >
            Back to Release Flows
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleBack}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              Back
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {isEditing ? 'Edit Release Flow' : 'New Release Flow'}
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                {isEditing ? `Editing ${releaseFlow?.name}` : 'Create a new release flow'}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {isSaving && (
              <span className="text-sm text-gray-600">Saving...</span>
            )}
            {isValidating && (
              <span className="text-sm text-gray-600">Validating...</span>
            )}
          </div>
        </div>

        {/* Form fields */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Release Flow Name *
            </label>
            <input
              type="text"
              id="name"
              value={releaseFlowName}
              onChange={(e) => setReleaseFlowName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter release flow name"
              required
            />
          </div>
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <input
              type="text"
              id="description"
              value={releaseFlowDescription}
              onChange={(e) => setReleaseFlowDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter description (optional)"
            />
          </div>
        </div>
      </div>

      {/* Validation Results */}
      {showValidation && validationResult && (
        <div className="bg-white border-b border-gray-200 px-6 py-3">
          <div className="flex items-start space-x-3">
            {validationResult.isValid ? (
              <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5" />
            ) : (
              <XCircleIcon className="h-5 w-5 text-red-500 mt-0.5" />
            )}
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <h3 className={`text-sm font-medium ${
                  validationResult.isValid ? 'text-green-800' : 'text-red-800'
                }`}>
                  {validationResult.isValid ? 'Validation Passed' : 'Validation Failed'}
                </h3>
                <button
                  onClick={() => setShowValidation(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-4 w-4" />
                </button>
              </div>
              {validationResult.validationErrors.length > 0 && (
                <ul className="mt-1 text-sm text-red-700">
                  {validationResult.validationErrors.map((error: string, index: number) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              )}
              {validationResult.warnings.length > 0 && (
                <ul className="mt-1 text-sm text-yellow-700">
                  {validationResult.warnings.map((warning: string, index: number) => (
                    <li key={index}>• {warning}</li>
                  ))}
                </ul>
              )}
              {validationResult.topologicalOrder.length > 0 && (
                <p className="mt-1 text-sm text-gray-600">
                  Execution order: {validationResult.topologicalOrder.length} nodes
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Editor */}
      <div className="flex-1">
        <ReleaseFlowEditorWithProvider
          releaseFlow={releaseFlow}
          onSave={handleSave}
          onValidate={handleValidate}
        />
      </div>
    </div>
  );
};